import { createHash } from 'crypto';
import fs from 'fs/promises';
import path from 'path';

import envPaths from 'env-paths';

import type { ResolvedConfig } from '../types';
import { logger } from '../log/logger';

import {
  DEFAULT_HASH_BATCH_SIZE,
  DEFAULT_HEARTBEAT_CHECK_INTERVAL_MS,
  DEFAULT_HEARTBEAT_INTERVAL_MS,
  DEFAULT_MAX_SNAPSHOT_ATTEMPTS,
  DEFAULT_QUEUE_POLL_INTERVAL_MS,
  DEFAULT_SNAPSHOT_DEBOUNCE_MS,
  DEFAULT_HTTP_TIMEOUT_MS,
  DEFAULT_UPLOAD_CHUNK_SIZE,
  DEFAULT_MAX_QUERY_WAIT_MS,
} from './Defaults';
import { resolveRootPath } from './RootResolver';

const DEFAULT_RETRIEVAL_FORMATTER: 'standard' | 'compact' = 'standard';

const DEFAULTS: Pick<
  ResolvedConfig,
  | 'snapshotDebounceMs'
  | 'heartbeatIntervalMs'
  | 'heartbeatCheckIntervalMs'
  | 'queuePollIntervalMs'
  | 'hashBatchSize'
  | 'maxSnapshotAttempts'
> = {
  snapshotDebounceMs: DEFAULT_SNAPSHOT_DEBOUNCE_MS,
  heartbeatIntervalMs: DEFAULT_HEARTBEAT_INTERVAL_MS,
  heartbeatCheckIntervalMs: DEFAULT_HEARTBEAT_CHECK_INTERVAL_MS,
  queuePollIntervalMs: DEFAULT_QUEUE_POLL_INTERVAL_MS,
  hashBatchSize: DEFAULT_HASH_BATCH_SIZE,
  maxSnapshotAttempts: DEFAULT_MAX_SNAPSHOT_ATTEMPTS,
};

function normalizeRoot(root: string): string {
  const resolved = path.resolve(root);
  const normalized = path.normalize(resolved);
  return normalized.split(path.sep).join('/');
}

function sha256(input: string): string {
  return createHash('sha256').update(input).digest('hex');
}

function parseInteger(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed) || parsed <= 0) {
    throw new Error(`Invalid integer value: ${value}`);
  }
  return parsed;
}

function parseSecondsToMs(
  value: string | undefined,
  fallbackMs: number,
): number {
  if (!value) return fallbackMs;
  const seconds = Number.parseInt(value, 10);
  if (Number.isNaN(seconds) || seconds <= 0) {
    throw new Error(`Invalid seconds value: ${value}`);
  }
  return seconds * 1000;
}

function parseFormatter(value: string | undefined): 'standard' | 'compact' {
  if (!value) return DEFAULT_RETRIEVAL_FORMATTER;
  const normalized = value.toLowerCase();
  if (normalized === 'standard' || normalized === 'compact') {
    return normalized;
  }
  throw new Error(
    `Invalid CODERULE_RETRIEVAL_FORMATTER: ${value}. Expected "standard" or "compact"`,
  );
}

export interface ResolveConfigParams {
  token?: string;
  rootPath?: string;
}

export async function resolveConfig({
  token,
  rootPath: cliRoot,
}: ResolveConfigParams): Promise<ResolvedConfig> {
  const resolvedToken = token ?? process.env.CODERULE_TOKEN;
  if (!resolvedToken) {
    throw new Error(
      'Missing token: provide params.token or CODERULE_TOKEN env',
    );
  }

  // Resolve root path using confidence-based resolution
  const rootCandidate = await resolveRootPath({
    cliRoot,
    logger: logger.child({ scope: 'root-resolver' }),
  });
  const rootPath = rootCandidate.path;

  const normalized = normalizeRoot(rootPath);
  const rootId = sha256(normalized);

  const dataDir = process.env.CODERULE_DATA_DIR || envPaths('coderule').data;
  const watchDir = path.join(dataDir, 'watch');
  await fs.mkdir(watchDir, { recursive: true });
  const dbPath = path.join(watchDir, `${rootId}.sqlite`);

  const baseConfig: ResolvedConfig = {
    token: resolvedToken,
    rootPath,
    rootId,
    dbPath,
    dataDir,
    authBaseUrl: process.env.CODERULE_AUTH_URL,
    astBaseUrl: process.env.CODERULE_AST_URL,
    syncBaseUrl: process.env.CODERULE_SYNC_URL,
    retrievalBaseUrl: process.env.CODERULE_RETRIEVAL_URL,
    httpTimeout: undefined,
    snapshotDebounceMs: DEFAULTS.snapshotDebounceMs,
    heartbeatIntervalMs: DEFAULTS.heartbeatIntervalMs,
    heartbeatCheckIntervalMs: DEFAULTS.heartbeatCheckIntervalMs,
    queuePollIntervalMs: DEFAULTS.queuePollIntervalMs,
    hashBatchSize: DEFAULTS.hashBatchSize,
    maxSnapshotAttempts: DEFAULTS.maxSnapshotAttempts,
    retrievalFormatter: parseFormatter(
      process.env.CODERULE_RETRIEVAL_FORMATTER,
    ),
    uploadChunkSize: DEFAULT_UPLOAD_CHUNK_SIZE,
    maxQueryWaitMs: DEFAULT_MAX_QUERY_WAIT_MS,
  };

  if (process.env.CODERULE_SNAPSHOT_DEBOUNCE_MS) {
    baseConfig.snapshotDebounceMs = parseInteger(
      process.env.CODERULE_SNAPSHOT_DEBOUNCE_MS,
      baseConfig.snapshotDebounceMs,
    );
  }
  if (process.env.CODERULE_HEARTBEAT_INTERVAL_MS) {
    baseConfig.heartbeatIntervalMs = parseInteger(
      process.env.CODERULE_HEARTBEAT_INTERVAL_MS,
      baseConfig.heartbeatIntervalMs,
    );
  }
  if (process.env.CODERULE_HEARTBEAT_CHECK_INTERVAL_MS) {
    baseConfig.heartbeatCheckIntervalMs = parseInteger(
      process.env.CODERULE_HEARTBEAT_CHECK_INTERVAL_MS,
      baseConfig.heartbeatCheckIntervalMs,
    );
  }
  if (process.env.CODERULE_QUEUE_POLL_INTERVAL_MS) {
    baseConfig.queuePollIntervalMs = parseInteger(
      process.env.CODERULE_QUEUE_POLL_INTERVAL_MS,
      baseConfig.queuePollIntervalMs,
    );
  }
  if (process.env.CODERULE_HASH_BATCH_SIZE) {
    baseConfig.hashBatchSize = parseInteger(
      process.env.CODERULE_HASH_BATCH_SIZE,
      baseConfig.hashBatchSize,
    );
  }
  if (process.env.CODERULE_MAX_SNAPSHOT_ATTEMPTS) {
    baseConfig.maxSnapshotAttempts = parseInteger(
      process.env.CODERULE_MAX_SNAPSHOT_ATTEMPTS,
      baseConfig.maxSnapshotAttempts,
    );
  }

  baseConfig.httpTimeout = parseInteger(
    process.env.CODERULE_HTTP_TIMEOUT,
    DEFAULT_HTTP_TIMEOUT_MS,
  );

  if (process.env.CODERULE_UPLOAD_CHUNK_SIZE) {
    baseConfig.uploadChunkSize = parseInteger(
      process.env.CODERULE_UPLOAD_CHUNK_SIZE,
      baseConfig.uploadChunkSize,
    );
  }

  baseConfig.maxQueryWaitMs = parseSecondsToMs(
    process.env.CODERULE_MAX_WAIT_TIME,
    baseConfig.maxQueryWaitMs,
  );

  logger.debug(
    {
      rootPath,
      rootSource: rootCandidate.source,
      rootConfidence: rootCandidate.finalConfidence,
      rootModifiers: rootCandidate.modifiers,
      dbPath,
      dataDir,
      authBaseUrl: baseConfig.authBaseUrl,
      astBaseUrl: baseConfig.astBaseUrl,
      syncBaseUrl: baseConfig.syncBaseUrl,
    },
    'Resolved configuration',
  );

  return baseConfig;
}
